import type { AxisSelection } from '@atlas/types'
import { AxisCode, ClassifiedObjectType, DeliverableType, FileType } from '@atlas/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { WorkOrderDetail } from '@/domain/work-order.types'
import { invalidateClassifiedReads } from '@/hooks/useClassification'
import { apiErrorMessage } from '@/lib/api-error'
import { triggerBrowserDownload } from '@/lib/browser-download'
import { logger } from '@/lib/logger'
import { notification } from '@/notifications/notification-store'
import { type ClassifyTuple, classificationApi } from '@/services/classification.service'
import { type FileWithTagsDTO, fileService } from '@/services/file.service'
import { type ValidationAssetRequestInput, workOrderApi } from '@/services/work-order.service'
import { enqueueClassified, tupleTags } from '@/upload/enqueue-with-tuples'
import { getItems as getUploadItems, removeItem as removeUploadItem } from '@/upload/upload-store'
import { useWorkOrderDetail, WORK_ORDER_DETAIL_KEY, workOrderDetailKey } from './useWorkOrders'
import { invalidateWorkOrderActivity } from './useWorkOrderTimeline'

/** Root of the work-order-files key family. Exported so the realtime query bridge invalidates the whole family. */
export const WORK_ORDER_FILES_KEY = 'work-order-files'

/** Query key for a work order's attached files. Exported so other hooks and the realtime query bridge invalidate the same key. */
export const workOrderFilesKey = (orderCode: string) => [WORK_ORDER_FILES_KEY, orderCode]

/**
 * Refresh the deliverables a mutation just changed.
 *
 * All five types live in one `work-order-detail` entry, so this is a single
 * invalidation refetching a single request — where invalidating the old
 * per-type keys re-fired five near-identical `/files/discover` calls at once.
 *
 * Falls back to the bare prefix when the caller only knows the order code: the
 * aggregate is keyed by work-order id, and prefix-matching still reaches the
 * order on screen because react-query refetches only observed queries.
 *
 * The package manifests go with it: a manifest is a read OVER the deliverables, so a
 * deliverable that changed and a package still listing the old set are the same staleness.
 */
const invalidateDeliverables = (qc: ReturnType<typeof useQueryClient>, workOrderId?: string) => {
	void qc.invalidateQueries({ queryKey: workOrderId ? workOrderDetailKey(workOrderId) : [WORK_ORDER_DETAIL_KEY] })
	void qc.invalidateQueries({ queryKey: [WORK_ORDER_PACKAGE_KEY] })
}

/**
 * Read a file's `deliverable_order` attribute off its classification tags (the
 * value is stored as a string). Missing/unparseable → `Number.MAX_SAFE_INTEGER`
 * so unordered files sort last.
 */
export function deliverableOrderOf(file: FileWithTagsDTO): number {
	const tag = file.tags?.find((t) => t.axisCode === AxisCode.DELIVERABLE_ORDER)
	const n = tag ? Number(tag.valueCode) : Number.NaN
	return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER
}

/**
 * The files attached to a work order — discovered by the `order_code`
 * attribute the upload flow stamps on each file. `order_code` is an
 * `object_attribute` (free-text), NOT a dimension, so it is passed as an
 * ATTRIBUTE filter (the dimension `filters` arg stays empty). Empty until finals
 * are uploaded. Kept out of the generic library `['files']` cache so board/detail
 * invalidations stay independent.
 */
export const useWorkOrderFiles = (orderCode: string | null) =>
	useQuery<FileWithTagsDTO[]>({
		queryKey: workOrderFilesKey(orderCode ?? ''),
		queryFn: () => fileService.discover({}, { [AxisCode.ORDER_CODE]: [orderCode as string] } as AxisSelection),
		enabled: !!orderCode,
	})

/**
 * A work order's deliverables of one `deliverable_type`.
 *
 * A SELECTOR over the single `work-order-detail` query, not a request of its
 * own. The five boxes previously each issued a `/files/discover` call differing
 * only by `deliverable_type`, and every deliverable mutation invalidates the key
 * as a bare prefix — so one upload re-fired all five at once. Reading five slices
 * of one cached object means one invalidation refetches one request.
 *
 * `visual_export` is sorted by the `deliverable_order` attribute (missing = last)
 * so the visual grid reflects the persisted order.
 *
 * Takes the work order's `id`, not its `orderCode`: the aggregate is addressed by
 * id like every other work-order read, and the page has both.
 */
export const useWorkOrderDeliverables = (workOrderId: string | null, deliverableType: DeliverableType) =>
	useWorkOrderDetail(workOrderId, (detail) => {
		const files = detail.deliverables[deliverableType] ?? []
		return deliverableType === DeliverableType.FINAL_VISUAL
			? [...files].sort((a, b) => deliverableOrderOf(a) - deliverableOrderOf(b))
			: files
	})

/**
 * Upload a deliverable of a given `deliverable_type` to a work order.
 * Mirrors `useUploadFinal` (presign → PUT → confirm → classify the tuple +
 * `order_code` + audit) but ALSO stamps `file_type=deliverable` and the given
 * `deliverable_type` via `classifyBatch`. For `visual_export` it seeds an initial
 * `deliverable_order` (max existing order + 1) as an attribute so the visual
 * grid has a stable order from the first drop.
 */
/**
 * Classify a freshly-uploaded deliverable file: file_type=deliverable +
 * deliverable_type + the order tuple (dimensions via classifyBatch), the
 * order_code attribute, and — for a visual_export — an initial deliverable_order
 * (max existing order + 1) so the visual grid has a stable order from the first drop.
 */
function deliverableTags(fileId: string, tuple: AxisSelection, deliverableType: DeliverableType): ClassifyTuple[] {
	return [
		{
			objectType: ClassifiedObjectType.FILE,
			objectId: fileId,
			axisCode: AxisCode.FILE_TYPE,
			valueCode: FileType.DELIVERABLE,
		},
		{
			objectType: ClassifiedObjectType.FILE,
			objectId: fileId,
			axisCode: AxisCode.DELIVERABLE_TYPE,
			valueCode: deliverableType,
		},
		...tupleTags(fileId, tuple),
	]
}

/**
 * The deliverable follow-ups that are NOT classification: the `order_code`
 * attribute (free text, so it can't ride `classifyBatch`) and, for a
 * `visual_export`, an initial `deliverable_order` so the grid has a stable order
 * from the first drop.
 */
async function applyDeliverableAttributes(
	fileId: string,
	deliverableType: DeliverableType,
	orderCode: string,
): Promise<void> {
	await classificationApi.setAttribute(ClassifiedObjectType.FILE, fileId, AxisCode.ORDER_CODE, [orderCode])
	if (deliverableType === DeliverableType.FINAL_VISUAL) {
		const existing = await fileService.discover(
			{ [AxisCode.FILE_TYPE]: [FileType.DELIVERABLE], [AxisCode.DELIVERABLE_TYPE]: [deliverableType] },
			{ [AxisCode.ORDER_CODE]: [orderCode] } as AxisSelection,
		)
		// Seed as (max existing order + 1), NOT the count: max+1 survives gaps left
		// by deletions and avoids two concurrent uploads reading the same count and
		// persisting the same order. `deliverableOrderOf` returns MAX_SAFE_INTEGER
		// for files with no order yet — exclude those (only finite orders count)
		// so the sentinel can't blow up nextIndex; start at 0 when none are ordered.
		// Best-effort: two concurrent uploads can still read the same max and collide
		// — a server-side sequence would be needed to fully close that race.
		const orders = existing
			.filter((f) => f.fileId !== fileId)
			.map(deliverableOrderOf)
			.filter((n) => Number.isFinite(n) && n < Number.MAX_SAFE_INTEGER)
		const nextIndex = orders.length ? Math.max(...orders) + 1 : 0
		await classificationApi.setAttribute(ClassifiedObjectType.FILE, fileId, AxisCode.DELIVERABLE_ORDER, [
			String(nextIndex),
		])
	}
}

/**
 * Upload-store owner tag for a deliverable drop zone.
 *
 * Exported so the zone and the mutation derive it from one place — if they ever
 * disagreed, the zone would silently render an empty tile list while the upload
 * ran invisibly.
 */
export const deliverableOwner = (orderCode: string, deliverableType: DeliverableType): string =>
	`wo-deliverable:${deliverableType}:${orderCode}`

/**
 * Owner tag for brand-kit slot uploads. Not order-scoped: a brand asset belongs
 * to the tuple, not to the order it happened to be uploaded from.
 */
export const BRAND_KIT_UPLOAD_OWNER = 'wo-brand-kit'

/**
 * Owner tag for guideline imports from the workspace's Guidelines tab.
 *
 * Distinct from {@link BRAND_KIT_UPLOAD_OWNER} even though both create the same kind of
 * asset: the detail page mounts the resolved-context panel and the workspace at the same
 * time, and the upload store is global, so one shared owner would render every guideline
 * import's tile in both places.
 */
export const GUIDELINES_UPLOAD_OWNER = 'wo-guidelines'

/**
 * Each guideline group is its own drop surface, so its in-flight uploads render as tiles in
 * that group and nowhere else.
 */
export const guidelinesUploadOwner = (fileType: string) => `${GUIDELINES_UPLOAD_OWNER}:${fileType}`

/** Every guideline import at once, whichever group it was dropped on. */
export const isGuidelinesUploadOwner = (owner: string | undefined): boolean =>
	Boolean(owner?.startsWith(`${GUIDELINES_UPLOAD_OWNER}:`))

export const useUploadDeliverable = (
	workOrderId: string,
	orderCode: string,
	tuple: AxisSelection,
	deliverableType: DeliverableType,
	{ isActive }: { isActive?: boolean } = {},
) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ file }: { file: File }) =>
			enqueueClassified(file, (fileId) => deliverableTags(fileId, tuple, deliverableType), {
				isActive,
				// Runs once the classification concern is terminal — succeeded, failed,
				// or never created — so the ordering read below doesn't race the
				// classification write, and the attachment still happens either way.
				afterClassified: async (confirmed) => {
					await applyDeliverableAttributes(confirmed.fileId, deliverableType, orderCode)
					await workOrderApi.recordFileAttached(workOrderId, confirmed.fileId)
					invalidateDeliverables(qc, workOrderId)
					// Discovery by `order_code` finds the file only now that the attribute is set.
					void qc.invalidateQueries({ queryKey: workOrderFilesKey(orderCode) })
					invalidateWorkOrderActivity(qc, workOrderId)
				},
				// The upload itself succeeded, so this can't fail the mutation — but a
				// missing order_code or audit entry is silent corruption if unreported.
				onFollowUpError: (error) =>
					notification.error(apiErrorMessage(error, 'The file uploaded, but attaching it failed.')),
				onClassificationFailed: (errorMessage) =>
					notification.error(`The file was attached, but tagging it failed: ${errorMessage ?? 'unknown error'}`),
				// Must match the owner the drop zone filters on, so its tiles appear
				// in the box the file was dropped on and nowhere else.
				owner: deliverableOwner(orderCode, deliverableType),
			}),
		onSuccess: () => {
			invalidateDeliverables(qc, workOrderId)
			void qc.invalidateQueries({ queryKey: workOrderFilesKey(orderCode) })
			void qc.invalidateQueries({ queryKey: ['work-order-context'] })
			invalidateWorkOrderActivity(qc, workOrderId)
			// Classified on the way in, so the file is now discoverable well beyond this order.
			invalidateClassifiedReads(qc)
		},
	})
}

/**
 * Upload a design file to a work order as a draft: a `visual_export` deliverable created
 * inactive, so it never counts as the order's design until the designer submits it.
 */
export const useUploadDesignFile = (workOrderId: string, orderCode: string, tuple: AxisSelection) =>
	useUploadDeliverable(workOrderId, orderCode, tuple, DeliverableType.FINAL_VISUAL, { isActive: false })

/**
 * Persist a new order for the `visual_export` deliverables. `deliverable_order`
 * is an `object_attribute` (number stored as string), so each file's index is
 * written via `setAttribute`. The UI decides WHEN to persist (on drop, debounced);
 * this hook only writes the given ordered `fileIds` and refreshes the visual list.
 * On failure it invalidates the same list so the grid refetches the TRUE persisted
 * order — reverting the optimistic local order the caller applied on drag.
 */
export const useReorderDeliverables = (orderCode: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (fileIds: string[]) => {
			await Promise.all(
				fileIds.map((fileId, index) =>
					classificationApi.setAttribute(ClassifiedObjectType.FILE, fileId, AxisCode.DELIVERABLE_ORDER, [
						String(index),
					]),
				),
			)
		},
		onSuccess: () => invalidateDeliverables(qc),
		onError: (error) => {
			logger.error('[deliverable-reorder] persist failed; refetching true order', { orderCode, error })
			invalidateDeliverables(qc)
		},
	})
}

/**
 * Request a validation asset for a work order: POST the assetType + channel;
 * the server gates on ≥1 final-visual deliverable AND ≥1 message, creating a
 * `validation_asset_request` row + emitting `work_order.validation_asset_requested`
 * (the pdf-generator-worker renders it). Invalidates the order's activity feed on
 * success so the request appears.
 *
 * A failing gate comes back as a 422 whose body carries the reason, which is
 * raised as an error notification here rather than left to each caller: the
 * button that triggers this has no other way to explain why nothing happened.
 */
export function useRequestValidationAsset(workOrderId: string) {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (body: ValidationAssetRequestInput) => workOrderApi.requestValidationAsset(workOrderId, body),
		onSuccess: () => {
			invalidateWorkOrderActivity(qc, workOrderId)
			void qc.invalidateQueries({ queryKey: ['work-order-validation-assets', workOrderId] })
		},
		onError: (error) => {
			notification.error(apiErrorMessage(error, 'Could not request the validation asset.'))
		},
	})
}

/**
 * Upload a final asset to a work order: presigned
 * `upload-url` → PUT to S3 → `confirm-upload` → classify the new file so it
 * becomes a deliverable discoverable from the order. The dimension tuple goes
 * through `classifyBatch` (registered axis values); `order_code` is a free-text
 * `object_attribute`, so it is written via `setAttribute` — sending it through
 * classifyBatch throws (its value isn't a registered `axis_value`) and would
 * fail the whole batch. Returns per-file progress via `onProgress`.
 *
 * `extraTags` classifies the file beyond the tuple, `attributes` stamps free-text attributes
 * beside `order_code` (a campaign's `campaign_code`, say), and `owner` names the upload surface.
 */
export const useUploadFinal = (
	workOrderId: string,
	orderCode: string,
	tuple: AxisSelection,
	{
		extraTags,
		attributes = {},
		owner,
	}: { extraTags?: (fileId: string) => ClassifyTuple[]; attributes?: AxisSelection; owner?: string } = {},
) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ file }: { file: File }) =>
			enqueueClassified(file, (fileId) => [...(extraTags?.(fileId) ?? []), ...tupleTags(fileId, tuple)], {
				afterClassified: async (confirmed) => {
					// `order_code` is a free-text `object_attribute`, so it can't ride
					// `classifyBatch` — sending it there throws (its value isn't a
					// registered `axis_value`) and would fail the whole batch.
					await classificationApi.setAttribute(ClassifiedObjectType.FILE, confirmed.fileId, AxisCode.ORDER_CODE, [
						orderCode,
					])
					for (const [axisCode, values] of Object.entries(attributes)) {
						await classificationApi.setAttribute(ClassifiedObjectType.FILE, confirmed.fileId, axisCode, values)
					}
					// Audit the attachment against the order so it shows in the activity feed.
					await workOrderApi.recordFileAttached(workOrderId, confirmed.fileId)
					void qc.invalidateQueries({ queryKey: workOrderFilesKey(orderCode) })
					invalidateWorkOrderActivity(qc, workOrderId)
				},
				onFollowUpError: (error) =>
					notification.error(apiErrorMessage(error, 'The file uploaded, but attaching it failed.')),
				onClassificationFailed: (errorMessage) =>
					notification.error(`The file was attached, but tagging it failed: ${errorMessage ?? 'unknown error'}`),
				owner,
			}),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: workOrderFilesKey(orderCode) })
			void qc.invalidateQueries({ queryKey: ['work-order-context'] })
			invalidateWorkOrderActivity(qc, workOrderId)
			invalidateClassifiedReads(qc)
		},
	})
}

/**
 * In-place brand-kit upload for a missing slot: presigned
 * upload → classify with the order's tuple + the slot's `file_type` (NOT
 * `order_code` — this is a brand asset for the tuple, not a deliverable of this
 * order). Invalidates the resolved context so the slot flips to available.
 *
 * `owner` names the SURFACE the uploads belong to, not this hook: two surfaces on one page
 * both creating brand assets must not render each other's tiles.
 */
export const useUploadBrandKitSlot = (tuple: AxisSelection, owner: string = BRAND_KIT_UPLOAD_OWNER) => {
	const qc = useQueryClient()
	return useMutation({
		// No `order_code` and no audit entry: this is a brand asset for the tuple,
		// not a deliverable of any one order. Classification IS the whole job here,
		// which is exactly why its failure has to be reported — an unclassified
		// brand asset is invisible to every slot lookup that would use it.
		mutationFn: ({ file, fileType, owner: fileOwner }: { file: File; fileType: string; owner?: string }) =>
			enqueueClassified(
				file,
				(fileId) => [
					{
						objectType: ClassifiedObjectType.FILE,
						objectId: fileId,
						axisCode: AxisCode.FILE_TYPE,
						valueCode: fileType,
					},
					...tupleTags(fileId, tuple),
				],
				{
					// The tags are applied by the processing worker AFTER confirm, so
					// invalidating at mutation success would refetch the file while it's
					// still untagged — every classified read lands it in no group at all.
					// Waiting for the concern to go terminal is what makes the import appear.
					// A brand-kit file is brand-wide content: it lands on the brand and
					// product pages too, hence the unkeyed `invalidateClassifiedReads`.
					afterClassified: () => {
						void qc.invalidateQueries({ queryKey: ['work-order-context'] })
						invalidateClassifiedReads(qc)
					},
					onClassificationFailed: (errorMessage) =>
						notification.error(`The brand asset uploaded, but tagging it failed: ${errorMessage ?? 'unknown error'}`),
					// Must match the owner the calling surface's tiles filter on. Without it every
					// brand-kit item carried `owner: undefined`, the filter never matched, and the
					// whole progress/retry block rendered nothing — the user picked a file and saw
					// no feedback at all. Overridable per file, for a surface whose groups each
					// show their own uploads rather than pooling them in one list.
					owner: fileOwner ?? owner,
				},
			),
	})
}

/** Delete an attached file. */
export const useDeleteWorkOrderFile = (orderCode: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (fileId: string) => fileService.deleteFile(fileId),

		// Optimistic: the row disappears on click, and the request runs behind it.
		// Waiting for the refetch meant the card sat there looking unresponsive for
		// as long as discovery took — seconds, when the classification read is slow.
		onMutate: async (fileId: string) => {
			const fileListKey = workOrderFilesKey(orderCode)
			const detailKey = [WORK_ORDER_DETAIL_KEY]
			// Stop in-flight reads from landing on top of the optimistic removal.
			await Promise.all([qc.cancelQueries({ queryKey: fileListKey }), qc.cancelQueries({ queryKey: detailKey })])

			// Two shapes to edit: the flat file list, and the detail aggregate, whose
			// deliverables are grouped by type — so the file is removed from whichever
			// bucket holds it rather than from a top-level array.
			const fileListEntries = qc.getQueriesData<FileWithTagsDTO[]>({ queryKey: fileListKey })
			const detailEntries = qc.getQueriesData<WorkOrderDetail>({ queryKey: detailKey })

			// Captured before the row is removed: the notification should name the
			// file the user recognises, not the uuid they never saw.
			const fileName =
				[
					...fileListEntries.flatMap(([, files]) => files ?? []),
					...detailEntries.flatMap(([, detail]) => Object.values(detail?.deliverables ?? {}).flat()),
				].find((file) => file.fileId === fileId)?.fileName ?? fileId

			qc.setQueriesData<FileWithTagsDTO[]>({ queryKey: fileListKey }, (files) =>
				files ? files.filter((file) => file.fileId !== fileId) : files,
			)
			qc.setQueriesData<WorkOrderDetail>({ queryKey: detailKey }, (detail) => {
				if (!detail) return detail
				const deliverables = Object.fromEntries(
					Object.entries(detail.deliverables).map(([type, files]) => [
						type,
						files.filter((file) => file.fileId !== fileId),
					]),
				)
				return { ...detail, deliverables }
			})
			// A finished upload stays in the store, hidden only by the server row sharing its
			// fileId; with that row gone it would resurface, pointing at the deleted object.
			for (const item of getUploadItems()) if (item.fileId === fileId) removeUploadItem(item.id)
			return { fileListEntries, detailEntries, fileName }
		},

		onError: (error, fileId, context) => {
			// Put every list back exactly as it was — the file still exists.
			for (const [queryKey, data] of context?.fileListEntries ?? []) qc.setQueryData(queryKey, data)
			for (const [queryKey, data] of context?.detailEntries ?? []) qc.setQueryData(queryKey, data)
			notification.error(apiErrorMessage(error, `Could not delete ${context?.fileName ?? fileId}.`))
		},

		onSuccess: (_data, fileId, context) => {
			notification.success(`Deleted ${context?.fileName ?? fileId}.`)
			void qc.invalidateQueries({ queryKey: workOrderFilesKey(orderCode) })
			invalidateDeliverables(qc)
			// A deleted file leaves discovery everywhere, not just this order's lists.
			invalidateClassifiedReads(qc)
		},
	})
}

/** Download a single file by its pre-signed URL. */
export async function downloadFile(fileId: string): Promise<void> {
	const url = await fileService.getDownloadUrl(fileId)
	triggerBrowserDownload(url)
}

/** Root of the package-manifest key family. Exported so the realtime query bridge invalidates it. */
export const WORK_ORDER_PACKAGE_KEY = 'work-order-package'

/** Query key for one order's distribution-package manifest. */
export const distributionPackageKey = (workOrderId: string) => [WORK_ORDER_PACKAGE_KEY, workOrderId, 'distribution']

/**
 * The candidate files of a work order's distribution package.
 *
 * A READ, not an action. The manifest is what the operator composes the package FROM — every
 * file that could ship, described well enough to be shown as a tile — so producing the package
 * is a separate gesture over a subset of this, never a side effect of asking what is in it.
 *
 * There is deliberately no fallback for an empty manifest. An order with nothing to package
 * must stay blocked: `artifact_exists(distribution_zip)` exists to stop the step reading as
 * done while nothing was produced, and a stand-in file satisfies the guard while producing
 * exactly that.
 */
export const useDistributionPackage = (workOrderId: string | null) =>
	useQuery({
		queryKey: distributionPackageKey(workOrderId ?? ''),
		queryFn: () => workOrderApi.packageManifest(workOrderId as string, 'distribution'),
		enabled: !!workOrderId,
	})
